/**
 * Can this saved mission be launched RIGHT NOW, and if not, why not — in the operator's words.
 *
 * WHY THIS EXISTS. Prod, 2026-08-30: an operator at the dock pressed LAUNCH twice on a mission
 * flown four days earlier (`completed`), 44 seconds apart. Both presses were refused 409
 * `mission_not_launchable`. The picker had offered it with a live LAUNCH button because it
 * fetched `/api/missions` with no filter, so the only thing that knew the mission could never
 * fly was the server — and it only said so after the press, in a toast on a ~4 s timer.
 *
 * THE AUTHORITY IS STILL THE SERVER. This does not replace a gate; it mirrors one, so the
 * surface can refuse BEFORE an operator arms a launch instead of after. The mirror is ordered
 * to match `launchMissionNow` (`src/services/mission-launch.ts`) gate for gate — status, then
 * the type's flyability, then the stored route against that type — because the first refusal
 * the server would give is the one the operator needs to read.
 *
 * FAILS OPEN ON WHAT IT CANNOT SEE. Every field but `status` is optional, following the
 * precedent `coverage_held_open` set in `missionDetailShared`: an older server, a narrower
 * projection or a cached response may not carry `type` or `waypoint_count`, and an unknown is
 * read as "no objection" rather than as a block. The cost of that choice is the pre-existing
 * behaviour — a refusal the operator finds out about at the press. The cost of the opposite
 * would be a picker that greys out a launchable mission because a field went missing, which on
 * a C2 display is strictly worse: it takes a working sortie off the board with no way to
 * override it.
 */

import { aMissionType } from './copy';
import { ORBIT_AIRFRAME_UNSUPPORTED, ORBIT_DOCK_UNLAUNCHABLE } from './copy/mission';
import { COVERAGE_UNREAD_COPY } from './copy/status';
import { commandRouteOf } from './drone-command-availability';
import {
  MISSION_TYPES,
  MISSION_TYPE_LABELS,
  UNFLYABLE_MISSION_TYPES,
  waypointCountRangeForType,
  type MissionType,
} from './mission-type';

/**
 * The statuses a mission may be launched FROM, mirroring `LAUNCHABLE_STATUSES` in
 * `src/services/mission-launch.ts`. `in_progress` is deliberately absent — it is launchable
 * only under the coverage hold below, which is a fact about other tables that no status can
 * express.
 */
export const LAUNCHABLE_STATUSES: ReadonlySet<string> = new Set(['draft', 'scheduled']);

/** Mirrors `MAX_LAUNCH_WAYPOINTS` in `src/services/mission-launch.ts`. */
export const MAX_LAUNCH_WAYPOINTS = 100;

/**
 * What a launch surface needs to know about a saved mission. Satisfied as-is by a row of
 * `GET /api/missions`, which selects `m.*` and decorates it with `waypoint_count` and
 * `coverage_held_open`.
 */
export interface LaunchableMission {
  status: string;
  /** Absent on a payload that predates 022. */
  type?: string | null;
  /** Only meaningful for a `hover`. Null means open-ended, which flies. */
  hover_seconds?: number | null;
  /** The count of STORED waypoints, as `findByOrg` computes it. */
  waypoint_count?: number | null;
  /** Computed server-side by `withCoverageHold`; the client cannot derive it. */
  coverage_held_open?: boolean;
}

function asMissionType(value: string | null | undefined): MissionType | null {
  return (MISSION_TYPES as readonly string[]).includes(value ?? '')
    ? (value as MissionType)
    : null;
}

/**
 * Why the server would refuse to launch this mission, or null when it would accept it.
 *
 * THE ONE `in_progress` MISSION THAT MAY LAUNCH AGAIN (#386). Continuous coverage holds a
 * mission open past its aircraft's landing on purpose, and the next aircraft in the rotation is
 * launched onto the mission that is ALREADY RUNNING rather than onto a copy of it. Filtering
 * `in_progress` out of the picker wholesale would therefore hide exactly the mission an
 * operator needs to relaunch mid-rotation — so the hold is asked about instead of guessed at.
 */
/** The aircraft a launch would use, in the two fields an ORBIT's eligibility turns on. */
export interface LaunchAircraft {
  /** `drones.dock_id`. Non-null means the platform would normally orchestrate its recovery. */
  dock_id?: string | null;
  command_route?: string | null;
  manufacturer?: string | null;
  integration_config?: Record<string, unknown> | null;
}

/** What the launch would use, so a surface can predict the refusals that depend on it. */
export interface LaunchContext {
  /**
   * The airframe. Absent means "not known here", and the orbit checks below are then skipped
   * rather than guessed — the server still refuses, and a surface that invents a refusal for an
   * aircraft it cannot see is worse than one that lets the server answer.
   */
  aircraft?: LaunchAircraft | null;
}

/**
 * Why this aircraft cannot fly an ORBIT, or null if it can.
 *
 * Mirrors the two server refusals, in the same order (`dockedOrbitRefusal` and the command-route
 * check in `src/services/mission-launch.ts`). The server is the authority; this exists so a
 * surface can say so BEFORE an operator arms a launch it would reject.
 *
 * ASKED OF THE AIRFRAME, not of the door. An earlier cut asked "is this the dock card?", which is
 * not the rule: a dock-REGISTERED aircraft that is already airborne resolves no dock, so it would
 * have passed a door-shaped check and been refused by the server anyway. `drones.dock_id` is what
 * decides, on both sides.
 */
export function orbitAircraftRefusal(
  type: MissionType,
  aircraft: LaunchAircraft | null | undefined,
): string | null {
  if (type !== 'orbit' || !aircraft) return null;
  if (aircraft.dock_id) return ORBIT_DOCK_UNLAUNCHABLE;
  const route = commandRouteOf(aircraft);
  // `null` means the row did not say — treated as "cannot tell", so the server answers rather
  // than the surface inventing a refusal for an aircraft it could not classify.
  if (route !== null && route !== 'dji_companion') return ORBIT_AIRFRAME_UNSUPPORTED;
  return null;
}

export function missionLaunchBlockReason(
  m: LaunchableMission,
  opts?: LaunchContext,
): string | null {
  const relaunchingCoverage = m.status === 'in_progress' && m.coverage_held_open === true;
  if (!LAUNCHABLE_STATUSES.has(m.status) && !relaunchingCoverage) {
    /**
     * AN UNREAD HOLD IS NOT "ALREADY IN PROGRESS" (#471). The server omits `coverage_held_open`
     * when its coverage lookup threw, so on a covered station awaiting its next aircraft this
     * used to print the flatly false reason "Already in progress" - on the dock LAUNCH picker,
     * which is where a mid-rotation relaunch actually happens (golden flow steps 4-6).
     *
     * STILL BLOCKED, ONLY HONEST ABOUT WHY. This file's doc comment says it "fails open on what
     * it cannot see", and that stays true of every field it can reason about; but this one field
     * is what SEPARATES the relaunchable covered mission from the ordinary running one, so
     * without it the launch cannot be judged at all. The refusal is the safe direction - a
     * relaunch is a real aircraft leaving a pad - and the operator is told to retry rather than
     * sent looking for a sortie that is not there.
     */
    if (m.status === 'in_progress' && m.coverage_held_open === undefined) {
      return COVERAGE_UNREAD_COPY.retry;
    }
    if (m.status === 'in_progress') return 'Already in progress';
    if (m.status === 'completed') return 'Completed — cannot be relaunched';
    if (m.status === 'cancelled') return 'Cancelled — cannot be launched';
    return `A ${m.status} mission cannot be launched`;
  }

  const type = asMissionType(m.type);
  if (type) {
    const unflyable = UNFLYABLE_MISSION_TYPES[type];
    if (unflyable) return unflyable;
    // An ORBIT flies, but not on every airframe: not off a dock (a circle has no end, so the
    // mission-finished ack that starts the automatic dock return never comes), and not on a
    // plane with no circular primitive. Said HERE rather than discovered at LAUNCH, which is
    // #394's whole complaint about launch pickers.
    const orbitIssue = orbitAircraftRefusal(type, opts?.aircraft);
    if (orbitIssue) return orbitIssue;
    // A HOVER with a STATED duration would be flown as an indefinite one — nothing between the
    // plan and the aircraft carries a hold time. Mirrors `missionTypeFlightIssue`.
    if (type === 'hover' && (m.hover_seconds ?? null) !== null) {
      return 'A set hover duration cannot be flown yet — clear it to fly open-ended';
    }
  }

  const count = m.waypoint_count;
  if (typeof count === 'number') {
    if (count === 0) return 'No route — draw one before launching';
    if (count > MAX_LAUNCH_WAYPOINTS) {
      return `Route has ${count} waypoints (maximum ${MAX_LAUNCH_WAYPOINTS})`;
    }
    if (type) {
      const { min, max } = waypointCountRangeForType(type);
      if (count < min || count > max) {
        return min === max
          ? `${aMissionType(type, { capitalised: true })} takes exactly ${min} point, not ${count}`
          : `${aMissionType(type, { capitalised: true })} takes ${min}–${max} waypoints, not ${count}`;
      }
    }
  }

  return null;
}

/** Convenience inverse of `missionLaunchBlockReason`, for a call site that only needs the bit. */
export function isMissionLaunchable(m: LaunchableMission, opts?: LaunchContext): boolean {
  return missionLaunchBlockReason(m, opts) === null;
}

/**
 * The statuses a launch picker should ASK the server for.
 *
 * Narrows the list at the source so the terminal statuses — the ones no coverage hold or
 * future edit can make launchable again — never reach the client at all. `in_progress` is
 * fetched because it MAY be launchable (the hold above), and `missionLaunchBlockReason`
 * decides each row from there.
 */
export const LAUNCH_PICKER_STATUSES = ['draft', 'scheduled', 'in_progress'] as const;
